/**
 * ActiveSubscriptionsPanel
 * ------------------------
 * Lists every active/trialing/past_due subscription on a client's account
 * with a "Cancel" button per row. Coach-side cancel — Sachin spec
 * 2026-02-17: "Cancel both on the athlete side and my side."
 *
 * Parent-side cancel already exists at /api/me/cancel-membership
 * (CancelMembershipModal). This is the matching coach button.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, X, Loader2, Calendar, AlertTriangle, HelpCircle, CheckCircle2, User, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; }
};


/**
 * WorkoutAccessDiagnosticButton
 * -----------------------------
 * Opens a popover with the EXACT reason a client does/doesn't have
 * workout-library access — useful when the owner can't tell whether
 * a parent's subscription is actually granting access (Sam Schwartz
 * pattern). Backend: GET /api/coach/clients/{user_id}/workout-access-diagnostic
 */
function WorkoutAccessDiagnosticButton({ userId }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const run = async () => {
        if (!userId) return;
        setBusy(true);
        try {
            const r = await axios.get(
                `${API}/coach/clients/${userId}/workout-access-diagnostic`,
                { withCredentials: true },
            );
            setData(r.data);
            setOpen(true);
        } catch (e) {
            toast.error("Diagnostic failed", { description: e?.response?.data?.detail || "Server error" });
        } finally { setBusy(false); }
    };

    return (
        <>
            <button
                type="button"
                data-testid="workout-access-diagnostic-btn"
                onClick={run}
                disabled={busy}
                title="Show whether this client has workout access and why"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:border-amber-600 hover:text-amber-200 px-2 py-1 disabled:opacity-50"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <HelpCircle className="w-3 h-3" />}
                Why locked?
            </button>
            {open && data && (
                <div
                    data-testid="workout-access-diagnostic-panel"
                    className="col-span-full w-full border border-amber-700/40 bg-zinc-950 p-3 mt-2"
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-amber-200">
                            Workout Access Diagnostic
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-zinc-500 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div
                        data-testid="workout-access-verdict"
                        className={`text-sm p-2.5 mb-3 border ${
                            data.has_access
                                ? "border-emerald-700/40 bg-emerald-950/20 text-emerald-200"
                                : "border-red-700/40 bg-red-950/20 text-red-200"
                        } flex items-start gap-2`}
                    >
                        {data.has_access
                            ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                        <div>{data.verdict}</div>
                    </div>
                    <div className="text-[10px] text-zinc-500 mb-2 grid grid-cols-2 gap-1">
                        <div>role: <span className="text-zinc-300 font-mono">{data.user?.role || "—"}</span></div>
                        <div>tier: <span className="text-zinc-300 font-mono">{data.user?.tier || "—"} {data.user?.tier_manual ? "(manual)" : ""}</span></div>
                        <div>status: <span className="text-zinc-300 font-mono">{data.user?.status || "—"}</span></div>
                        <div>rank: <span className="text-zinc-300 font-mono">{data.rank}</span></div>
                    </div>
                    {data.subscriptions?.length > 0 ? (
                        <div className="space-y-1.5 mt-2">
                            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400">
                                Subscriptions ({data.subscriptions.length})
                            </div>
                            {data.subscriptions.map((s, i) => (
                                <div
                                    key={s.subscription_id || s.stripe_subscription_id || i}
                                    className={`text-[11px] border px-2 py-1.5 ${
                                        s.unlocks_workouts
                                            ? "border-emerald-700/40 bg-emerald-950/15"
                                            : "border-zinc-800 bg-zinc-900/30"
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span className={s.unlocks_workouts ? "text-emerald-300" : "text-zinc-400"}>
                                            {s.unlocks_workouts ? "✓" : "✗"}
                                        </span>
                                        <span className="font-mono text-zinc-300">{s.plan_name || `${(s.price_cents || 0) / 100}/${s.interval}`}</span>
                                        <span className="text-zinc-500">·</span>
                                        <span className="uppercase tracking-[0.15em] text-zinc-400">{s.status}</span>
                                    </div>
                                    <div className="text-[10px] text-zinc-500 mt-0.5">{s.reason}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-zinc-400 italic">No subscriptions found on this user.</div>
                    )}
                </div>
            )}
        </>
    );
}



// SKU → human-friendly commitment plan summary. Owner spec 2026-02-17 —
// the coach needs to instantly see WHICH tier a member chose (3-wk $16 /
// 6-wk $14 / 12-wk $12) without digging into Stripe.
const SKU_PLAN = {
    sub_12wk: { label: "12-week plan", commitment_weeks: 12 },
    sub_15wk_6wk_commit: { label: "6-week plan", commitment_weeks: 6 },
    sub_1750wk_3wk_commit: { label: "3-week plan", commitment_weeks: 3 },
};

function planDescriptor(sub) {
    const meta = SKU_PLAN[sub.sku_id];
    if (meta) return meta;
    // Best-effort from plan_name string ("3-Week Plan · $16/wk")
    const m = (sub.plan_name || "").match(/(\d+)\s*-?\s*week/i);
    if (m) return { label: `${m[1]}-week plan`, commitment_weeks: parseInt(m[1], 10) };
    return { label: "Week-to-week", commitment_weeks: null };
}

function commitmentProgress(sub, plan) {
    if (!plan?.commitment_weeks || !sub.created_at) return null;
    const startMs = new Date(sub.created_at).getTime();
    if (!Number.isFinite(startMs)) return null;
    const weeksElapsed = Math.max(0, Math.floor((Date.now() - startMs) / (7 * 24 * 60 * 60 * 1000)));
    const remaining = Math.max(0, plan.commitment_weeks - weeksElapsed);
    return {
        weeksElapsed: Math.min(weeksElapsed + 1, plan.commitment_weeks),
        totalWeeks: plan.commitment_weeks,
        weeksRemaining: remaining,
        complete: remaining === 0,
    };
}

export function SubRow({ userId, sub, athletes = [], onChanged }) {
    const [busy, setBusy] = useState(false);
    const [reassigning, setReassigning] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const cancelled = !!sub.cancel_at_period_end;
    const stripeId = sub.stripe_subscription_id || sub.subscription_id;
    const plan = planDescriptor(sub);
    const progress = commitmentProgress(sub, plan);

    // Resolve assigned athlete from the family roster
    const assignedAthlete = sub.athlete_id
        ? athletes.find((a) => a.athlete_id === sub.athlete_id)
        : null;
    const assignedLabel = assignedAthlete
        ? `${assignedAthlete.first_name || ""} ${assignedAthlete.last_name || ""}`.trim()
        : null;
    // Sachin 2026-02-27 — Spotify-Plan model: untagged subs (legacy
    // pre-per-athlete) need owner attention. Strong amber/yellow chip
    // copy so the coach can spot them at a glance.
    const needsAssignment = !sub.athlete_id;

    const reassign = async (newAthleteId) => {
        if (reassigning) return;
        const target = newAthleteId ? athletes.find((a) => a.athlete_id === newAthleteId) : null;
        const label = target ? `${target.first_name} ${target.last_name || ""}`.trim() : "(untagged — whole family)";
        if (!window.confirm(`Reassign this subscription to ${label}?\n\nThis controls which athlete unlocks workouts and gets per-athlete attendance credit.`)) return;
        setReassigning(true);
        try {
            await axios.post(
                `${API}/coach/clients/${userId}/subscriptions/${stripeId}/assign-athlete`,
                { athlete_id: newAthleteId || null },
                { withCredentials: true },
            );
            toast.success(newAthleteId ? `Assigned to ${label}` : "Assignment cleared (now untagged)");
            setPickerOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't reassign", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setReassigning(false);
        }
    };

    const doCancel = async (atPeriodEnd) => {
        const verb = atPeriodEnd ? "Cancel at period end" : "Cancel NOW (immediate)";
        const consequence = atPeriodEnd
            ? `They'll keep access until ${fmtDate(sub.current_period_end)}, then auto-stop.`
            : `Stripe immediately stops billing. NO refund of the current period (Stripe rule). They lose access at next attendance check.`;
        if (!window.confirm(`${verb}?\n\n${sub.plan_name || "Subscription"} · ${fmtMoney(sub.price_cents)}/${sub.interval}\n\n${consequence}`)) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/coach/clients/${userId}/subscriptions/${stripeId}/cancel`,
                { at_period_end: atPeriodEnd },
                { withCredentials: true },
            );
            toast.success(atPeriodEnd ? "Subscription will end at period close" : "Subscription cancelled immediately");
            onChanged?.({ removed: !atPeriodEnd, subscriptionId: sub.subscription_id });
        } catch (e) {
            toast.error("Couldn't cancel", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setBusy(false); }
    };

    return (
        <div
            data-testid={`sub-row-${sub.subscription_id}`}
            className={`flex items-center gap-3 px-3 py-2.5 border-b border-zinc-900 last:border-b-0 ${cancelled ? "bg-amber-900/10" : "hover:bg-zinc-900/30"}`}
        >
            <Zap className={`w-4 h-4 flex-shrink-0 ${cancelled ? "text-amber-400" : "text-emerald-400"}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-semibold truncate flex items-baseline gap-1.5" data-testid="sub-row-plan">
                    <span className="text-emerald-300 font-mono">{fmtMoney(sub.price_cents)}/wk</span>
                    <span className="text-zinc-500">·</span>
                    <span className="text-zinc-200">{plan.label}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span data-testid="sub-row-status" className="uppercase tracking-[0.15em]">{sub.status}</span>
                    {athletes.length > 0 && (
                        <>
                            <span className="text-zinc-700">·</span>
                            <span className="relative inline-flex items-center">
                                <button
                                    type="button"
                                    onClick={() => setPickerOpen((v) => !v)}
                                    disabled={reassigning}
                                    data-testid={`sub-row-for-${sub.subscription_id}`}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] tracking-[0.1em] uppercase border ${
                                        assignedAthlete
                                            ? "border-emerald-700/50 bg-emerald-950/30 text-emerald-300 hover:border-emerald-500"
                                            : "border-amber-500/70 bg-amber-900/30 text-amber-200 font-semibold hover:border-amber-400 animate-pulse"
                                    }`}
                                    title={needsAssignment
                                        ? "This sub isn't tied to a specific athlete yet — click to assign it."
                                        : "Reassign which athlete this subscription unlocks"}
                                >
                                    <User className="w-2.5 h-2.5" />
                                    {needsAssignment
                                        ? "Needs assignment"
                                        : <>For: {assignedLabel}</>}
                                    <ChevronDown className="w-2.5 h-2.5" />
                                    {reassigning && <Loader2 className="w-2.5 h-2.5 animate-spin ml-0.5" />}
                                </button>
                                {pickerOpen && (
                                    <div className="absolute top-full left-0 mt-1 z-30 bg-zinc-950 border border-zinc-700 shadow-2xl min-w-[200px] py-1" onClick={(e) => e.stopPropagation()}>
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 px-2.5 py-1">Assign to…</div>
                                        {athletes.map((a) => {
                                            const isCurrent = a.athlete_id === sub.athlete_id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={a.athlete_id}
                                                    onClick={() => reassign(a.athlete_id)}
                                                    disabled={isCurrent || reassigning}
                                                    data-testid={`sub-row-reassign-${a.athlete_id}`}
                                                    className={`block w-full text-left px-2.5 py-1.5 text-[11px] disabled:opacity-50 ${
                                                        isCurrent ? "bg-emerald-950/40 text-emerald-300" : "hover:bg-zinc-800"
                                                    }`}
                                                >
                                                    {isCurrent && "✓ "}
                                                    {a.first_name} {a.last_name || ""}
                                                    {a.has_assigned_sub && !isCurrent && (
                                                        <span className="ml-1 text-[9px] text-zinc-500">(already has a sub)</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                        <div className="border-t border-zinc-800 my-1" />
                                        <button
                                            type="button"
                                            onClick={() => reassign(null)}
                                            disabled={reassigning || !sub.athlete_id}
                                            className="block w-full text-left px-2.5 py-1.5 text-[11px] text-amber-300 hover:bg-zinc-800 disabled:opacity-50"
                                        >
                                            Clear assignment (untag)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPickerOpen(false)}
                                            className="block w-full text-left px-2.5 py-1 text-[10px] text-zinc-500 hover:bg-zinc-800"
                                        >
                                            Close
                                        </button>
                                    </div>
                                )}
                            </span>
                        </>
                    )}
                    {progress && (
                        <>
                            <span className="text-zinc-700">·</span>
                            <span data-testid="sub-row-progress" className={progress.complete ? "text-amber-300" : "text-zinc-300"}>
                                {progress.complete
                                    ? `commitment complete — auto-renews unless cancelled`
                                    : `week ${progress.weeksElapsed} of ${progress.totalWeeks} · ${progress.weeksRemaining} left in commit`}
                            </span>
                        </>
                    )}
                    {sub.current_period_end && (
                        <>
                            <span className="text-zinc-700">·</span>
                            <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {cancelled ? "ends" : "renews"} {fmtDate(sub.current_period_end)}
                            </span>
                        </>
                    )}
                    {cancelled && (
                        <span className="ml-1 text-amber-300 font-semibold">· cancellation scheduled</span>
                    )}
                </div>
            </div>
            {!cancelled ? (
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        data-testid={`sub-cancel-period-end-${sub.subscription_id}`}
                        onClick={() => doCancel(true)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-amber-700/60 bg-amber-900/30 text-amber-200 hover:bg-amber-800/40 px-2.5 py-1.5 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        End at period
                    </button>
                    <button
                        type="button"
                        data-testid={`sub-cancel-now-${sub.subscription_id}`}
                        onClick={() => doCancel(false)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-red-700/60 bg-red-900/30 text-red-200 hover:bg-red-800/40 px-2.5 py-1.5 disabled:opacity-50"
                        title="Cancel immediately — no refund of current period"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                        Now
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.18em] uppercase text-amber-300 px-1">
                        ends {fmtDate(sub.current_period_end)}
                    </span>
                    <button
                        type="button"
                        data-testid={`sub-end-now-${sub.subscription_id}`}
                        onClick={() => doCancel(false)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-red-700/60 bg-red-900/30 text-red-200 hover:bg-red-800/40 px-2.5 py-1.5 disabled:opacity-50"
                        title="Force cancel now — don't wait for the period to end"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                        End now
                    </button>
                </div>
            )}
        </div>
    );
}

export default function ActiveSubscriptionsPanel({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [athletes, setAthletes] = useState([]);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/clients/${userId}/subscription-status`, { withCredentials: true });
            setData(r.data || null);
        } catch { setData(null); }
        finally { setLoading(false); }
    }, [userId]);

    const loadAthletes = useCallback(async () => {
        if (!userId) return;
        try {
            const r = await axios.get(`${API}/coach/clients/${userId}/family-athletes`, { withCredentials: true });
            setAthletes(r.data?.athletes || []);
        } catch { setAthletes([]); }
    }, [userId]);

    useEffect(() => { load(); loadAthletes(); }, [load, loadAthletes]);

    if (loading || !data || !data.subscriptions || data.subscriptions.length === 0) return null;

    return (
        <section
            data-testid="active-subs-panel"
            className="border border-emerald-700/30 bg-emerald-950/10 p-4 mt-4"
        >
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <h3 className="text-[11px] tracking-[0.28em] uppercase text-emerald-300 font-display font-bold flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-emerald-400" />
                    Weekly Subscriptions · {data.count}
                </h3>
                <WorkoutAccessDiagnosticButton userId={userId} />
            </div>
            <p className="text-[10px] text-zinc-500 mb-2 leading-snug">
                Recurring Stripe subscriptions — billed automatically every week/month. Separate from session packs below.
            </p>
            <div className="border border-zinc-900">
                {data.subscriptions.map((s) => (
                    <SubRow
                        key={s.subscription_id}
                        userId={userId}
                        sub={s}
                        athletes={athletes}
                        onChanged={() => { load(); loadAthletes(); }}
                    />
                ))}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                <strong className="text-zinc-300">End at period</strong> = athlete trains until current week ends, then auto-stops · <strong className="text-zinc-300">Now</strong> = Stripe stops billing immediately (no refund).
            </p>
            {athletes.length > 0 && (
                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                    <strong className="text-zinc-300">For:</strong> which athlete this sub unlocks workout access for.{" "}
                    <strong className="text-amber-300">Needs assignment</strong> (yellow chip) means the sub is legacy account-wide — click to assign it to a specific kid. Owner-only.
                </p>
            )}
        </section>
    );
}
